import { graphql } from 'gatsby';
import propType from 'prop-types';
import React from 'react';
import { Divider } from 'antd';
import HeaderNavigation from '../../components/HeaderNavigation';
import Recommendation from '../../components/Recommendation';
import ArticleBreadcrumb from './components/ArticleBreadcrumb';
import Footer from './components/Footer';
import SEO from './components/SEO';
import Content from '../../components/ArticleContent';
import AuthorInfo from '../../components/AuthorInfo';
import ShareArticle from '../../components/ShareArticle';
import Tags from './components/Tag';
import ArticleHeader from '../../components/ArticleHeader';

const BlogDetail = ({ data, location }) => {
  const {
    wpPost,
    site: {
      siteMetadata: { author: siteAuthor, keywords, siteUrl: domain }
    }
  } = data;

  const { href } = location;

  const {
    title,
    uri,
    excerpt,
    featuredImage,
    categories,
    content,
    author,
    dateGmt,
    customPostField: { keywords: seoKeywords },
    seo: { title: seoTitle, readingTime = 0 }
  } = wpPost;

  return (
    <>
      <SEO
        title={title}
        seoTitle={seoTitle}
        keywords={keywords}
        seoKeywords={seoKeywords}
        excerpt={excerpt}
        featuredImage={featuredImage}
        firstName={author?.node?.firstName}
        lastName={author?.node?.lastName}
        domain={domain}
        siteAuthor={siteAuthor}
        uri={uri}
      />
      <HeaderNavigation type="special" />
      <article id="blog-detail">
        <ArticleHeader
          type="blog"
          metadata={{ title, dateGmt, featuredImage }}
        />
        <main className="article-body__wrapper">
          <ArticleBreadcrumb
            title={title}
            breadcrumb={categories.nodes[0].seo.breadcrumbs}
          />
          <Content content={content} readingTime={readingTime} />
          <Tags
            tags={[
              {
                uri: categories.nodes[0].pillarPost.uri,
                name: categories.nodes[0].pillarPost.customPostField.topic
              }
            ]}
          />
          <Divider className="article-body__divider" />
          {author?.node?.name !== 'admin' && (
            <AuthorInfo author={author?.node} />
          )}
          <ShareArticle
            shareUrl={href}
            content={excerpt}
            hashtag={categories.nodes[0].name}
          />
        </main>
      </article>
      {wpPost.relatedPosts.length !== 0 && (
        <Recommendation
          title="Related Articles"
          articles={wpPost.relatedPosts}
        />
      )}
      <Footer />
    </>
  );
};

export default BlogDetail;

BlogDetail.propTypes = {
  data: propType.shape({
    site: propType.shape({
      siteMetadata: propType.shape({
        author: propType.string,
        keywords: propType.string,
        siteUrl: propType.string
      })
    }).isRequired,
    wpPost: propType.shape({
      author: propType.shape({
        node: propType.shape({
          name: propType.string.isRequired,
          firstName: propType.string.isRequired,
          lastName: propType.string.isRequired
        })
      }).isRequired,
      uri: propType.string.isRequired,
      content: propType.node.isRequired,
      dateGmt: propType.string.isRequired,
      title: propType.string.isRequired,
      excerpt: propType.string.isRequired,
      seo: propType.shape({
        title: propType.string,
        readingTime: propType.number
      }).isRequired,
      featuredImage: propType.shape({
        node: propType.shape({
          localFile: propType.shape({
            childImageSharp: propType.shape({
              gatsbyImageData: propType.shape({})
            })
          })
        })
      }).isRequired,
      tags: propType.shape({
        nodes: propType.arrayOf(
          propType.shape({
            name: propType.string.isRequired
          })
        ).isRequired
      }).isRequired,
      categories: propType.shape({
        nodes: propType.arrayOf(
          propType.shape({
            name: propType.string.isRequired,
            pillarPost: propType.shape({
              uri: propType.string.isRequired,
              customPostField: propType.shape({
                topic: propType.string.isRequired
              }).isRequired
            }),
            seo: propType.shape({
              breadcrumbs: propType.arrayOf({})
            })
          })
        ).isRequired
      }),
      customPostField: propType.shape({
        uri: propType.string,
        keywords: propType.string
      }),
      relatedPosts: propType.arrayOf(propType.shape({})).isRequired
    }).isRequired
  }).isRequired,
  location: propType.shape({
    href: propType.string.isRequired
  }).isRequired
};

export const query = graphql`
  query post($id: String!) {
    site {
      siteMetadata {
        description
        author
        keywords
        siteUrl
      }
    }
    wpPost(id: { eq: $id }) {
      title
      uri
      excerpt
      seo {
        readingTime
        title
      }
      author {
        node {
          avatar {
            url
          }
          name
          firstName
          lastName
          description
          user {
            occupation
          }
          localImage {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      categories {
        nodes {
          id
          slug
          uri
          name
          seo {
            breadcrumbs {
              text
              url
            }
          }
          pillarPost {
            id
            uri
            title
            customPostField {
              topic
            }
          }
        }
      }
      tags {
        nodes {
          id
          slug
          name
        }
      }
      dateGmt(formatString: "MMMM DD, YYYY")
      content
      featuredImage {
        node {
          localFile {
            ...HeroImage
          }
        }
      }

      customPostField {
        keywords
      }

      relatedPosts {
        title
        uri
        excerpt
        categories {
          nodes {
            id
            slug
            uri
            name
            pillarPost {
              id
              uri
              title
              customPostField {
                topic
              }
            }
          }
        }
        tags {
          nodes {
            id
            slug
            name
          }
        }
        featuredImage {
          node {
            localFile {
              ...Thumbnail
            }
          }
        }
      }
    }
  }
`;
